import { NavLink, Outlet, useLocation, Navigate } from 'react-router-dom';
import {
  FolderGit2,
  FolderKanban,
  KeyRound,
  LayoutGrid,
  Link2,
  Globe,
  Lock,
  Plug,
  ShieldCheck,
  Tags,
  Users,
  Users2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const SECTIONS = [
  {
    title: 'Workspace',
    items: [
      { to: '/settings/general', label: 'General', icon: Globe },
      { to: '/settings/members', label: 'Members', icon: Users },
      { to: '/settings/groups', label: 'Groups', icon: Users2 },
      { to: '/settings/spaces', label: 'Spaces', icon: LayoutGrid },
    ],
  },
  {
    title: 'Repositories',
    items: [
      { to: '/settings/github', label: 'GitHub', icon: Link2 },
      { to: '/settings/taxonomy', label: 'Taxonomy', icon: Tags },
      { to: '/settings/projects', label: 'Projects', icon: FolderKanban },
    ],
  },
  {
    title: 'Documentation',
    items: [
      { to: '/settings/repo-roles', label: 'Repository roles', icon: FolderGit2 },
      { to: '/settings/sanitization', label: 'Sanitization', icon: ShieldCheck },
    ],
  },
  {
    title: 'Developer',
    items: [
      { to: '/settings/connect', label: 'Connect', icon: Plug },
      { to: '/settings/api-keys', label: 'API keys', icon: KeyRound },
    ],
  },
  {
    title: 'Account',
    items: [
      { to: '/settings/security', label: 'Security', icon: Lock },
    ],
  },
];

export default function SettingsPage() {
  const location = useLocation();

  if (location.pathname === '/settings') {
    return <Navigate to="/settings/general" replace />;
  }

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Settings</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Manage your workspace, people, repositories, and integrations.
        </p>
      </div>

      <div className="flex gap-8 items-start">
        <nav className="w-52 shrink-0 space-y-6">
          {SECTIONS.map(({ title, items }) => (
            <div key={title}>
              <h2 className="px-3 mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                {title}
              </h2>
              <ul className="space-y-0.5">
                {items.map(({ to, label, icon: Icon }) => (
                  <li key={to}>
                    <NavLink
                      to={to}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-purple-50 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800/60'
                        )
                      }
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      {label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="flex-1 min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
